export const toBase64 = (str) => {
  try {
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    );
  } catch (e) {
    console.error("Failed to base64 encode string:", str, e);
    return btoa("invalid_filename");
  }
};

/**

© 2025 Indikin.online. All rights reserved.

Made with ❤️ by YalaCoder.

--- REWRITE SUMMARY (V115 - Per-Content Analytics API) ---

MISSION: To add a new service function for fetching the

per-content analytics summary needed for the "My Library" streams column.

CHANGES:

Added getPerContentAnalyticsSummary: Fetches 30-day aggregate

data grouped by video UID, calling the new /analytics/content-summary

worker endpoint.

RESULT: The DashboardProvider can now call this function to

fetch all stream counts for the library in a single, efficient batch.
*/

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const apiFetch = async (endpoint, options = {}, idToken = null) => {
  const cleanBase = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${cleanBase}${cleanEndpoint}`;



  const headers = {
    'Accept': 'application/json',
    ...options.headers,
  };

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const config = { ...options, headers };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData?.message || errorData?.error || `Request failed with status ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { success: true };
    }

    return await response.json();
  } catch (error) {
    console.error(`API Fetch Error for endpoint ${endpoint}:`, error);
    // Parse backend error if available
    if (error.message.includes('Failed to initiate') || error.message.includes('Invalid response')) {
      throw new Error(error.message); // Preserve backend-specific messages
    }
    throw error;
  }
};

// --- USER & NEWSLETTER FUNCTIONS ---
export const subscribeToNewsletter = (data, idToken) => apiFetch('/subscribe', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const submitToWhitelist = (data, idToken) => apiFetch('/whitelist', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const submitToEarnWhitelist = (data) => apiFetch('/earn-whitelist', { method: 'POST', body: JSON.stringify(data) });
export const checkEarnWhitelist = (data) => apiFetch('/check-earn-whitelist', { method: 'POST', body: JSON.stringify(data) });
export const submitFilmmakerApplication = (data, idToken) => apiFetch('/filmmaker-apply', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const getFilmmakers = (idToken) => apiFetch(`/filmmakers?_t=${Date.now()}`, {}, idToken);
export const sendWelcomeEmail = (idToken) => apiFetch('/user/welcome-email', { method: 'POST' }, idToken);
export const getUserPassStatus = (idToken) => apiFetch('/user-pass-status', { method: 'GET' }, idToken);
export const getUserPurchases = (idToken) => apiFetch('/user-purchases', { method: 'GET' }, idToken);
export const getFilmmakerTransactions = (idToken) => apiFetch('/filmmaker-transactions', { method: 'GET' }, idToken);
export const giftPass = (data, idToken) => apiFetch('/gift-pass', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const deleteUserAccount = (idToken) => apiFetch('/user/delete', { method: 'DELETE' }, idToken);
export const updateUserProfile = (idToken, data) => apiFetch('/user/profile', { method: 'PATCH', body: JSON.stringify(data) }, idToken);

// --- PAYMENT & COUPON FUNCTIONS ---
export const getAvailableCurrencies = (idToken) => apiFetch('/get-available-currencies', {}, idToken);
export const createPaymentInvoice = (data, idToken) => apiFetch('/create-payment-invoice', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const getPaymentStatus = (orderId, idToken) => apiFetch(`/payment-status?orderId=${encodeURIComponent(orderId)}`, {}, idToken);
export const createStripePaymentIntent = (data, idToken) => apiFetch('/stripe/create-payment-intent', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const redeemCoupon = (data, idToken) => apiFetch('/redeem-coupon', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const getPendingPaymentDetails = (orderId, idToken) => apiFetch(`/pending-payment-details?orderId=${orderId}`, {}, idToken);
export const redeemResumeToken = (token, idToken) => apiFetch('/redeem-resume-token', { method: 'POST', body: JSON.stringify({ token }) }, idToken);
export const createStripeTipIntent = (data) => apiFetch('/stripe/create-tip-intent', { method: 'POST', body: JSON.stringify(data) });

// --- TOKEN DATA FUNCTION ---
export const getTokenData = (pairs, idToken) => apiFetch('/token-data', { method: 'POST', body: JSON.stringify({ pairs }) }, idToken);

// --- FILM, SERIES, & STREAM MANAGEMENT FUNCTIONS ---
export const createSeries = (seriesData, idToken) => apiFetch('/series', { method: 'POST', body: JSON.stringify(seriesData) }, idToken);
export const initiateUrlUpload = (filmData, idToken) => apiFetch('/initiate-url-upload', { method: 'POST', body: JSON.stringify(filmData) }, idToken);

export const createDirectUploadFilmDocument = (filmData, idToken) => apiFetch('/create-direct-upload-film-document', { method: 'POST', body: JSON.stringify(filmData) }, idToken);



export const requestThumbnailUploadURL = (filmId, idToken) => apiFetch(`/film/${filmId}/request-thumbnail-upload`, { method: 'POST' }, idToken);

export const uploadSubtitleFile = (filmId, language, file, idToken) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiFetch(`/film/${filmId}/subtitle/${language}`, {
    method: 'PUT',
    body: formData,
  }, idToken);
};

export const deleteSubtitle = (filmId, subtitleId, idToken) => apiFetch(`/film/${filmId}/subtitle/${subtitleId}`, { method: 'DELETE' }, idToken);
export const getSignedPlaybackURL = (uid, idToken) => apiFetch(`/stream/${uid}/token`, { method: 'POST' }, idToken);
export const updateFilm = (filmId, updates, idToken) => apiFetch(`/film/${filmId}`, { method: 'PATCH', body: JSON.stringify(updates) }, idToken);

export const updateSeries = (seriesId, updates, idToken) => apiFetch(`/series/${seriesId}`, { method: 'PATCH', body: JSON.stringify(updates) }, idToken);
export const checkFilmStatus = (filmId, idToken) => apiFetch(`/film/${filmId}`, { method: 'POST' }, idToken);
export const deleteFilm = (filmId, idToken) => apiFetch(`/film/${filmId}`, { method: 'DELETE' }, idToken);
export const getUploadStatus = (filmId, idToken) => apiFetch(`/film/${filmId}/status`, { method: 'GET' }, idToken);

export const streamFilmIdea = async (prompt, idToken) => {
  const url = `${API_BASE_URL}/ai/filmmaker-chat`;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  };
  const body = JSON.stringify({ prompt });

  const response = await fetch(url, { method: 'POST', headers, body });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to get stream from AI service.');
  }
  return response.body;
};

export const streamVideoChat = async (prompt, videoId, idToken, contextData = {}) => {
  const url = `${API_BASE_URL}/ai/video-chat`;
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`;

  const body = JSON.stringify({ prompt, videoId, ...contextData });

  const response = await fetch(url, { method: 'POST', headers, body });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to get stream from AI service.');
  }
  return response.body;
};

export const getPublicShareData = (type, identifier) => apiFetch(`/public/share/${type}/${identifier}`, { method: 'GET' });

// --- NEW: Filmmaker Analytics Functions ---
export const getAnalyticsSummary = (idToken) => apiFetch('/analytics/summary', { method: 'GET' }, idToken);
export const getDailyAnalyticsSummary = (idToken) => apiFetch('/analytics/summary/daily', { method: 'GET' }, idToken);
export const getContentAnalytics = (uids, idToken) => apiFetch('/analytics/content', { method: 'POST', body: JSON.stringify({ uids }) }, idToken);

export const getPerContentAnalyticsSummary = (idToken) => apiFetch('/analytics/content-summary', { method: 'GET' }, idToken);

export const getTokenPrice = (address, pairAddress, isNative, idToken) => {
  const params = new URLSearchParams({
    address: address || '',
    pairAddress: pairAddress || '',
    isNative: isNative ? 'true' : 'false'
  });
  return apiFetch(`/get-token-price?${params.toString()}`, { method: 'GET' }, idToken);
};

export const checkWalletEmail = (wallet) => apiFetch(`/check-wallet-email?wallet=${wallet}`, { method: 'GET' });

export const initCryptoPayment = (data, idToken) =>
  apiFetch('/init-crypto-payment', { method: 'POST', body: JSON.stringify(data) }, idToken);

export const getPublicBalance = (wallet, token, idToken) => {
  const params = new URLSearchParams({ wallet });
  if (token) params.append('token', token);
  return apiFetch(`/get-public-balance?${params.toString()}`, { method: 'GET' }, idToken);
};

export const verifyCryptoPayment = (data, idToken) =>
  apiFetch('/verify-crypto-payment', { method: 'POST', body: JSON.stringify(data) }, idToken);

// --- NEW: Subtitle & AI Functions ---
export const downloadSubtitle = (filmId, language, idToken) => {
  const url = `${API_BASE_URL}/film/${filmId}/subtitle/${language}/download`;
  // We construct the URL for direct usage or fetch it to get a blob
  return apiFetch(url, { method: 'GET', headers: { 'Accept': 'text/vtt' } }, idToken);
};

export const generateAiCaptions = (filmId, language, idToken) =>
  apiFetch(`/film/${filmId}/captions/generate`, { method: 'POST', body: JSON.stringify({ language }) }, idToken);

export const pollAiCaptionStatus = (filmId, language, idToken) =>
  apiFetch(`/film/${filmId}/captions/draft-status?language=${language}`, { method: 'GET' }, idToken);

// --- Featured Films Trailer Management ---
export const getFeaturedFilms = () => apiFetch('/featured-films');

export const requestTrailerUpload = (idToken, data, fileSize) => apiFetch(
  '/featured-films/request-upload',
  {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Upload-Length': String(fileSize) },
  },
  idToken
);

export const requestFeaturedThumbnailUpload = (idToken, data) => apiFetch(
  '/featured-films/request-thumbnail-upload',
  { method: 'POST', body: JSON.stringify(data) },
  idToken
);

export const updateFeaturedFilm = (idToken, data) => apiFetch(
  '/featured-films/update',
  { method: 'PATCH', body: JSON.stringify(data) },
  idToken
);

export const reorderFeaturedFilms = (idToken, orderedFilmIds) => apiFetch(
  '/featured-films/reorder',
  { method: 'PATCH', body: JSON.stringify({ orderedFilmIds }) },
  idToken
);

export const deleteFeaturedFilm = (idToken, filmId) => apiFetch(
  '/featured-films/delete',
  { method: 'DELETE', body: JSON.stringify({ filmId }) },
  idToken
);

// --- AFFILIATE / AMBASSADOR ENDPOINTS ---
export const setupAffiliate = (idToken, username) => apiFetch('/affiliate/setup', { method: 'POST', body: JSON.stringify({ username }) }, idToken);
export const getAffiliateMe = (idToken) => apiFetch('/affiliate/me', { method: 'GET' }, idToken);
export const updateAffiliateMe = (idToken, data) => apiFetch('/affiliate/me', { method: 'PATCH', body: JSON.stringify(data) }, idToken);
export const getAffiliateCommissions = (idToken, page = 1) => apiFetch(`/affiliate/me/commissions?page=${page}`, { method: 'GET' }, idToken);
export const trackAffiliateClick = (code, metadata) => apiFetch('/affiliate/track-click', { method: 'POST', body: JSON.stringify({ code, ...metadata }) });
export const acceptAffiliateTerms = (idToken) => apiFetch('/affiliate/accept-terms', { method: 'POST' }, idToken);
export const getAffiliateLeaderboard = (idToken) => apiFetch('/affiliate/leaderboard', { method: 'GET' }, idToken);
export const checkAffiliateCode = (code) => apiFetch(`/affiliate/check-code/${code}`, { method: 'GET' });
export const resolveAffiliateCode = (code) => apiFetch(`/affiliate/resolve/${code}`, { method: 'GET' });
export const affiliateStripeOnboard = (idToken) => apiFetch('/affiliate/stripe-connect/onboard', { method: 'POST' }, idToken);
export const affiliateStripeStatus = (idToken) => apiFetch('/affiliate/stripe-connect/status', { method: 'GET' }, idToken);

// --- PHASE 3 ADMIN ENDPOINTS ---
export const adminGetAffiliates = (idToken) => apiFetch('/admin/affiliates', { method: 'GET' }, idToken);
export const adminGetPendingPayouts = (idToken) => apiFetch('/admin/payouts/pending', { method: 'GET' }, idToken);
export const adminCreatePayout = (idToken, data) => apiFetch('/admin/payouts', { method: 'POST', body: JSON.stringify(data) }, idToken);
export const adminGetAffiliateStats = (idToken, uid) => apiFetch(`/admin/affiliates/${uid}`, { method: 'GET' }, idToken);

// --- PHASE 2 CLIPS ENDPOINTS ---
export const getAffiliateClips = () => apiFetch('/affiliate/clips', { method: 'GET' });
export const adminUploadClip = (idToken, data) => apiFetch('/admin/clips', { method: 'POST', body: data }, idToken);
export const uploadClip = (idToken, data) => apiFetch('/clips/upload', { method: 'POST', body: data }, idToken);
export const updateClip = (idToken, id, data) => apiFetch(`/clips/update?id=${id}`, { method: 'PATCH', body: JSON.stringify(data) }, idToken);
export const adminUpdateClip = (idToken, id, data) => apiFetch(`/admin/clips?id=${id}`, { method: 'PATCH', body: JSON.stringify(data) }, idToken);
export const adminDeleteClip = (idToken, id) => apiFetch(`/admin/clips?id=${id}`, { method: 'DELETE' }, idToken);

// --- Revenue Config ---
export const saveRevenueConfigApi = (idToken, contentId, data) =>
  apiFetch(`/revenue-config/${contentId}`, { method: 'POST', body: JSON.stringify(data) }, idToken);

// --- Storymaker Stripe Connect ---
export const storymakerStripeOnboard = (idToken) =>
  apiFetch('/storymaker/stripe-connect/onboard', { method: 'POST' }, idToken);

export const getStorymakerStripeStatus = (idToken) =>
  apiFetch('/storymaker/stripe-connect/status', {}, idToken);

// --- Storymaker Terms ---
export const storymakerAcceptPayoutTerms = (idToken) =>
  apiFetch('/storymaker/accept-payout-terms', { method: 'POST' }, idToken);

// --- Storymaker Payout Settings ---
export const storymakerSavePayoutSettings = (idToken, data) =>
  apiFetch('/storymaker/payout-settings', {
    method: 'POST',
    body: JSON.stringify(data),
  }, idToken);

// --- Storymaker Earnings ---
export const getStorymakerEarningsSummary = (idToken) =>
  apiFetch('/storymaker/earnings/summary', {}, idToken);

// --- Admin: Storymaker Payouts ---
export const adminGetPendingStorymakerPayouts = (idToken) =>
  apiFetch('/admin/storymaker-payouts/pending', {}, idToken);

export const adminCreateStorymakerPayout = (idToken, payoutData) =>
  apiFetch('/admin/storymaker-payouts', {
    method: 'POST',
    body: JSON.stringify(payoutData),
  }, idToken);